import { useEffect, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Wind, Buildings, ArrowsClockwise, ArrowRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

export default function AdminFilterMasterList() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [q, setQ] = useState("");
  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/filter-master-list");
      setData(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);
  const props = (data?.properties || []).filter(p => {
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return p.address.toLowerCase().includes(t) || (p.unit || "").toLowerCase().includes(t) || (p.primary_size || "").includes(t);
  });
  return (
    <AdminShell>
      <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-4" data-testid="admin-filter-master-list">
        <div className="flex items-center gap-3 flex-wrap">
          <Wind weight="fill" size={28} className="text-cyan-600"/>
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">HVAC Filters</div>
            <h1 className="font-display font-black text-3xl">Filter Master List</h1>
          </div>
          <button onClick={load} disabled={busy}
            data-testid="filter-master-refresh"
            className="ml-auto h-9 px-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
            <ArrowsClockwise className={busy ? "animate-spin" : ""} size={12}/> Refresh
          </button>
        </div>
        <p className="text-sm text-slate-600 max-w-2xl">
          Every filter size captured across every inspection, grouped by property. Pull this
          before you place an order so the truck rolls with exactly what each unit needs.
        </p>

        {/* Portfolio totals */}
        {data?.totals?.length > 0 && (
          <div className="rugged-card p-4 rounded-2xl" data-testid="filter-master-totals">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Portfolio totals</div>
            <div className="flex flex-wrap gap-2">
              {data.totals.map(t => (
                <span key={t.size} className="inline-flex items-center h-9 px-3 rounded-full bg-cyan-100 text-cyan-900 font-black text-sm tracking-widest">
                  <span className="font-mono">{t.size}"</span>
                  <span className="ml-2 text-[10px] uppercase text-cyan-700">× {t.unit_count} unit{t.unit_count === 1 ? "" : "s"}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Search */}
        <div className="rugged-card p-3 rounded-2xl">
          <input list="known-addresses" autoComplete="off" value={q} onChange={e => setQ(e.target.value)}
            placeholder="Filter by address, unit, or size…"
            data-testid="filter-master-search"
            className="w-full h-11 px-3 rounded-xl border-2 border-slate-300 focus:border-cyan-500 outline-none text-sm"/>
        </div>

        {/* Property list */}
        {busy && !data ? (
          <div className="p-12 text-center text-sm text-slate-500 font-black uppercase tracking-widest">Loading…</div>
        ) : props.length === 0 ? (
          <div className="rugged-card p-12 rounded-2xl text-center">
            <Buildings weight="fill" size={40} className="mx-auto text-slate-300"/>
            <div className="mt-3 font-display font-black text-2xl">Nothing captured yet</div>
            <p className="mt-2 text-slate-500 text-sm max-w-md mx-auto">
              As soon as techs mark Filter → OK or Replaced and pick a size, that unit shows up here.
            </p>
          </div>
        ) : (
          <div className="rugged-card overflow-hidden rounded-2xl divide-y divide-slate-100" data-testid="filter-master-list">
            {props.map((p, i) => (
              <div key={`${p.address}-${p.unit || i}`} className="p-3 flex items-start gap-3" data-testid={`filter-master-row-${i}`}>
                <Buildings weight="fill" size={20} className="text-slate-500 mt-1 shrink-0"/>
                <div className="flex-1 min-w-0">
                  <div className="font-black">
                    {p.address}
                    {p.unit && <span className="ml-2 px-2 py-0.5 bg-fuchsia-600 text-white text-[10px] font-black uppercase tracking-widest rounded-full">#{p.unit}</span>}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {p.all_sizes.map(s => (
                      <span key={s.size}
                        className={`inline-flex items-center h-7 px-2 rounded-full text-[11px] font-black tracking-widest ${
                          s.size === p.primary_size ? "bg-cyan-600 text-white" : "bg-slate-100 text-slate-700"
                        }`}>
                        <span className="font-mono">{s.size}"</span>
                        {s.count > 1 && <span className="ml-1 opacity-70">×{s.count}</span>}
                      </span>
                    ))}
                  </div>
                  {p.rooms?.length > 0 && (
                    <div className="mt-1 text-[10px] text-slate-500">
                      Rooms: {p.rooms.slice(0, 4).map(r => `${r.label} (${r.size}")`).join(" · ")}
                      {p.rooms.length > 4 && ` · +${p.rooms.length - 4} more`}
                    </div>
                  )}
                </div>
                {p.property_id && (
                  <Link to={`/admin/properties/${p.property_id}`}
                    className="h-9 px-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
                    Open <ArrowRight weight="bold" size={11}/>
                  </Link>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminShell>
  );
}
